import React, { createContext, useContext, useRef, useCallback } from 'react';

/**
 * TokenContext - Secure In-Memory Token Storage
 * 
 * Security Strategy:
 * - Access Token: Stored ONLY in memory (React ref) - immune to XSS
 * - Refresh Token: Stored in HttpOnly cookie by backend - immune to XSS
 * - Tokens never touch localStorage/sessionStorage
 * 
 * Token Lifecycle:
 * 1. Login → Backend sets refreshToken cookie + returns access token
 * 2. Access token stored in memory (this context)
 * 3. On 401 → Axios interceptor calls /refresh-token (cookie sent automatically)
 * 4. New access token stored in memory
 * 5. Logout → Backend clears cookie, we clear memory
 */

const TokenContext = createContext(null);

export const TokenProvider = ({ children }) => {
  // Store access token in memory using ref (persists across renders but NOT page refreshes)
  const accessTokenRef = useRef(null);
  const userDataRef = useRef(null);

  /**
   * Set access token in memory
   * @param {string} token - JWT access token (2 hour expiry)
   */
  const setAccessToken = useCallback((token) => {
    accessTokenRef.current = token;
    console.log('🔐 Access token stored in memory');
  }, []);

  /**
   * Get current access token from memory
   * @returns {string|null}
   */
  const getAccessToken = useCallback(() => {
    return accessTokenRef.current;
  }, []);

  /**
   * Clear access token from memory (logout)
   */
  const clearAccessToken = useCallback(() => {
    accessTokenRef.current = null;
    userDataRef.current = null;
    console.log('🗑️ Access token cleared from memory');
  }, []);

  /**
   * Store user metadata (non-sensitive data like userID, email)
   * This is separate from the token itself
   */
  const setUserData = useCallback((userData) => {
    userDataRef.current = userData;
  }, []);

  /**
   * Get user metadata
   * @returns {object|null}
   */
  const getUserData = useCallback(() => {
    return userDataRef.current;
  }, []);

  /**
   * Check if user is authenticated (has valid token in memory)
   * Note: Token could be expired, axios interceptor will handle refresh
   */
  const isAuthenticated = useCallback(() => {
    return !!accessTokenRef.current;
  }, []);

  const value = {
    setAccessToken,
    getAccessToken,
    clearAccessToken,
    setUserData,
    getUserData,
    isAuthenticated,
  };

  return (
    <TokenContext.Provider value={value}>
      {children}
    </TokenContext.Provider>
  );
};

/**
 * Hook to access token context
 * @returns {object} Token management functions
 */
export const useToken = () => {
  const context = useContext(TokenContext);
  
  if (!context) {
    throw new Error('useToken must be used within TokenProvider');
  }
  
  return context;
};

export default TokenContext;
